import { z } from 'zod'

/** One command of a deploy (PROMPT.md §5.9). */
export const deployStepSchema = z.object({
  /** Shown in the log so a failure names something a person recognises. */
  label: z.string().trim().min(1, 'Der Schritt braucht einen Namen.').max(80),
  command: z.string().trim().min(1, 'Der Schritt braucht ein Kommando.'),
  /** Runs with sudo when the server needs it; the wrapper decides how (§5.3). */
  requiresSudo: z.boolean().default(false),
})
export type DeployStep = z.infer<typeof deployStepSchema>

export const projectSchema = z.object({
  id: z.string(),
  serverId: z.string(),
  name: z.string(),
  remotePath: z.string(),
  steps: z.array(deployStepSchema),
  healthUrl: z.string().nullable(),
  lastDeployAt: z.iso.datetime().nullable(),
  /** The commit the working tree was on before the last deploy — v1 has no rollback (§5.9). */
  lastDeployCommit: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})
export type Project = z.infer<typeof projectSchema>

const healthUrl = z
  .string()
  .trim()
  .url('Das ist keine gültige URL.')
  .nullable()
  .or(z.literal('').transform(() => null))

export const createProjectSchema = z.object({
  serverId: z.string().min(1),
  name: z.string().trim().min(1, 'Das Projekt braucht einen Namen.').max(80),
  remotePath: z
    .string()
    .trim()
    .min(1, 'Ohne Verzeichnis weiß Kontor nicht, wo das Projekt liegt.')
    .startsWith('/', 'Der Pfad muss mit / beginnen.'),
  steps: z.array(deployStepSchema).min(1, 'Ein Deploy ohne Schritte tut nichts.'),
  healthUrl: healthUrl.default(null),
})
export type CreateProjectInput = z.input<typeof createProjectSchema>

export const updateProjectSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  remotePath: z.string().trim().min(1).startsWith('/').optional(),
  steps: z.array(deployStepSchema).min(1).optional(),
  healthUrl: healthUrl.optional(),
})
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>

export const projectIdParamsSchema = z.object({ id: z.string().min(1) })

export const deployStatusSchema = z.enum(['running', 'ok', 'failed', 'cancelled'])
export type DeployStatus = z.infer<typeof deployStatusSchema>

export const deployStepResultSchema = z.object({
  label: z.string(),
  command: z.string(),
  status: z.enum(['pending', 'running', 'ok', 'failed', 'skipped']),
  exitCode: z.number().int().nullable(),
  durationMs: z.number().nonnegative().nullable(),
})
export type DeployStepResult = z.infer<typeof deployStepResultSchema>

export const deployRunSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  serverId: z.string(),
  status: deployStatusSchema,
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  steps: z.array(deployStepResultSchema),
  /** The commit the tree was on before the first step ran. */
  commitBefore: z.string().nullable(),
  commitAfter: z.string().nullable(),
  /** Null when the project has no health URL. */
  health: z
    .object({
      url: z.string(),
      ok: z.boolean(),
      status: z.number().int().nullable(),
      detail: z.string(),
    })
    .nullable(),
  message: z.string().nullable(),
})
export type DeployRun = z.infer<typeof deployRunSchema>

export const startDeploySchema = z.object({
  /** Answer for `sudo_mode = 'ask'`, kept only for this run. */
  sudoPassword: z.string().optional(),
})

/** Control frames on the deploy socket; output travels as binary, like the log stream. */
export const deploySocketMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('run'), run: deployRunSchema }),
  z.object({
    type: z.literal('step'),
    index: z.number().int().nonnegative(),
    run: deployRunSchema,
  }),
  z.object({ type: z.literal('done'), run: deployRunSchema }),
  z.object({ type: z.literal('error'), message: z.string() }),
])
export type DeploySocketMessage = z.infer<typeof deploySocketMessageSchema>
